// Sidebar navigation — pure helpers for the collapsible nav groups (2026-07-19).
//
// The sidebar had grown to ~18 items across 6 groups, which is more than anyone
// can scan. Groups now collapse, only the section you're working in is open, and
// the group containing the current route auto-expands on navigation so you never
// lose your place.
//
// Persistence uses localStorage (not sessionStorage like the inbox's feed
// controls): which nav sections you keep open is a durable personal layout
// preference, not per-session working state — it should survive a browser
// restart the way a collapsed folder tree does.
//
// ── SUB-HEADERS (2026-09-17) ──
// The menu reached 32 items across 9 groups and was regrouped BY JOB rather than
// by what built each feature. Some groups now carry a third level: a SUB-HEADER.
//
// ⚠️⚠️ A SUB-HEADER IS A LABEL, NOT A GROUP. It is a non-interactive heading
// inside an already-open group: it does NOT collapse and it has NO route. That
// is the scope decision, and it is what leaves the whole persistence layer below
// (normalizeOpenGroups / loadOpenGroups / saveOpenGroups / initialOpenGroups /
// applyRouteChange / toggleGroup and the `kyck-sidebar-open-groups` key)
// untouched — every one of them still keys on TOP-LEVEL group labels only.
// Making sub-headers independently collapsible means persisting a second level
// and is deliberately not built.
//
// ⚠️ Items and sub-headers share ONE FLAT `entries` list rather than the
// sub-header owning a nested array of its own. resolveActiveGroup /
// routeMatchScore / routeOwnershipScore all walk items looking for the best
// route match; a flat list means they filter to items and are otherwise
// unchanged, where nested arrays would mean rewriting all three — and
// resolveActiveGroup must keep answering with the TOP-LEVEL group (a route under
// a sub-header expands "Tools", not "AI bots").

export interface SidebarNavItem {
  to: string;
  label: string;
  end?: boolean;
  adminOnly?: boolean;
}

/**
 * A heading inside an open group. Never collapses, never navigates.
 *
 * ⚠️ `to?: never` is the discriminant: it makes `{ subheader, to }` a compile
 * error, so a sub-header cannot acquire a route by accident and quietly start
 * competing for route ownership.
 */
export interface SidebarNavSubheader {
  subheader: string;
  adminOnly?: boolean;
  to?: never;
}

export type SidebarNavEntry = SidebarNavItem | SidebarNavSubheader;

/**
 * ⚠️ Generic so the narrowing PRESERVES the caller's own item type. The sidebar
 * component's entries carry an `icon` on top of SidebarNavItem; a predicate
 * asserting the bare `entry is SidebarNavItem` would narrow that away and the
 * icon would stop compiling at the render site.
 *
 * ⚠️ And a plain `'to' in entry` cannot do this job: `to` is OPTIONAL on the
 * sub-header (it is `to?: never`), and TypeScript puts a constituent with an
 * optional property of that name into BOTH branches of an `in` check — so the
 * sub-header would survive into the item branch.
 */
export function isNavItem<E extends SidebarNavEntry>(
  entry: E,
): entry is Extract<E, SidebarNavItem> {
  return typeof (entry as SidebarNavItem).to === 'string';
}

/**
 * ⚠️ Generic over the entry type for the same reason `isNavItem` is: the sidebar
 * component's entries carry an `icon`, and a section type pinned to the bare
 * `SidebarNavEntry` would erase it the moment a list passed through
 * `filterNavForRole`.
 */
export interface SidebarNavSectionOf<E extends SidebarNavEntry> {
  label: string;
  /** Hide the WHOLE section from non-admins (Operational). See filterNavForRole. */
  adminOnly?: boolean;
  entries: E[];
}

export type SidebarNavSection = SidebarNavSectionOf<SidebarNavEntry>;

const OPEN_GROUPS_STORAGE_KEY = 'kyck-sidebar-open-groups';

/**
 * How specifically an item's route matches the current pathname, as a score
 * (higher = more specific); -1 = no match.
 *
 * `end` items match exactly only — that's what keeps "/" (Dashboard) and
 * "/reporting" from swallowing every nested route. Prefix matches require a
 * SEGMENT boundary, so "/publishing" does not match "/publishing-archive"
 * (and "/advertising/meta" does not match "/advertising/meta-setup"). This
 * mirrors react-router's own NavLink rule, which is what actually paints the
 * highlight.
 */
export function routeMatchScore(pathname: string, item: SidebarNavItem): number {
  if (pathname === item.to) return item.to.length + 1;
  if (item.end) return -1;
  const prefix = item.to.endsWith('/') ? item.to : `${item.to}/`;
  return pathname.startsWith(prefix) ? item.to.length : -1;
}

/**
 * How specifically an item OWNS a route, for group resolution.
 *
 * Deliberately ignores `end`: that flag governs LINK HIGHLIGHTING (it stops
 * "/" highlighting on every page), not which section a route belongs to.
 * Conflating the two broke the case that matters most — Publishing's Calendar
 * item is `end`, so editing a post at /publishing/:id/edit matched nothing and
 * the sidebar collapsed the section you were actively working in.
 */
function routeOwnershipScore(pathname: string, item: SidebarNavItem): number {
  if (pathname === item.to) return item.to.length + 1;
  // The root prefixes every path — it owns exactly itself, never a subtree.
  if (item.to === '/') return -1;
  const prefix = item.to.endsWith('/') ? item.to : `${item.to}/`;
  return pathname.startsWith(prefix) ? item.to.length : -1;
}

/**
 * The label of the group owning the best (most specific) match for the current
 * route, or null when nothing matches.
 *
 * ⚠️ Always a TOP-LEVEL group label, never a sub-header: sub-headers are skipped
 * outright, and the label returned is the section's. A route sitting under a
 * sub-header therefore expands the section that contains it.
 */
export function resolveActiveGroup(
  sections: SidebarNavSection[],
  pathname: string,
): string | null {
  let best = -1;
  let label: string | null = null;
  for (const section of sections) {
    for (const entry of section.entries) {
      if (!isNavItem(entry)) continue;
      const score = routeOwnershipScore(pathname, entry);
      if (score > best) {
        best = score;
        label = section.label;
      }
    }
  }
  return label;
}

/**
 * The nav as a given role sees it: sections, sub-headers and items an admin-only
 * flag hides are removed, and so is anything they leave empty behind them.
 *
 * ⚠️ Item-level filtering alone is NOT enough, which is what this function
 * exists for. Dropping every item out of Operational would otherwise leave a
 * non-admin looking at an empty "Operational" group header, and dropping every
 * item under a sub-header leaves a heading with nothing under it. Both read as
 * a broken menu rather than as a section they lack access to.
 */
export function filterNavForRole<E extends SidebarNavEntry>(
  sections: ReadonlyArray<SidebarNavSectionOf<E>>,
  isAdmin: boolean,
): SidebarNavSectionOf<E>[] {
  const visible = (x: { adminOnly?: boolean }) => !x.adminOnly || isAdmin;
  return sections
    .filter(visible)
    .map((section) => ({
      ...section,
      entries: pruneEmptySubheaders(section.entries.filter(visible)),
    }))
    // A section with only sub-headers left is empty in every sense that matters.
    .filter((section) => section.entries.some(isNavItem));
}

/**
 * Drop a sub-header with no item under it.
 *
 * Runs AFTER item filtering, so "has an item under it" is exactly "the next
 * surviving entry is an item" — anything else (another sub-header, or the end of
 * the list) means everything it headed has gone.
 */
function pruneEmptySubheaders<E extends SidebarNavEntry>(entries: E[]): E[] {
  return entries.filter((entry, i) => {
    if (isNavItem(entry)) return true;
    const next = entries[i + 1];
    return next !== undefined && isNavItem(next);
  });
}

/** Drop anything that isn't a currently-known group label. */
export function normalizeOpenGroups(raw: unknown, validLabels: string[]): string[] | null {
  if (!Array.isArray(raw)) return null;
  const valid = new Set(validLabels);
  return raw.filter((v): v is string => typeof v === 'string' && valid.has(v));
}

export function loadOpenGroups(validLabels: string[]): string[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_STORAGE_KEY);
    if (raw === null) return null;
    return normalizeOpenGroups(JSON.parse(raw), validLabels);
  } catch {
    return null;
  }
}

export function saveOpenGroups(labels: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(labels));
  } catch {
    // Persistence is best-effort.
  }
}

/**
 * The open set on first paint. With nothing stored, ONLY the current route's
 * group is open — that's the whole point of the change (truncate the list), and
 * it's the least surprising first impression: you see where you are.
 */
export function initialOpenGroups(
  stored: string[] | null,
  activeGroup: string | null,
): string[] {
  if (stored === null) return activeGroup ? [activeGroup] : [];
  return activeGroup && !stored.includes(activeGroup) ? [...stored, activeGroup] : stored;
}

/**
 * Fold a NAVIGATION into the open set: the group you just moved into opens.
 *
 * Applied only when the active group CHANGES — not on every render — so
 * collapsing the group you're currently in stays collapsed until you navigate
 * somewhere else. Force-opening every render would make that group impossible
 * to close.
 */
export function applyRouteChange(open: string[], activeGroup: string | null): string[] {
  if (!activeGroup || open.includes(activeGroup)) return open;
  return [...open, activeGroup];
}

export function toggleGroup(open: string[], label: string): string[] {
  return open.includes(label) ? open.filter((l) => l !== label) : [...open, label];
}
